import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { getUser } from "@/lib/auth";
import { AppHeader } from "@/components/AppHeader";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Plus, Pencil, Trash2, FolderKanban } from "lucide-react";

const emptyForm = () => ({
  name: "", code: "", client_name: "",
  product_unit_name: "Cup", product_price: 9,
  target_per_store: 400, geofence_m: 150,
});

export default function AdminProjects() {
  const user = getUser();
  const isSuper = user?.role === "SuperAdmin";
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openForm, setOpenForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm());
  const [confirmDel, setConfirmDel] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/projects");
      setProjects(data);
    } catch { toast.error("Failed to load"); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const openNew = () => { setEditing(null); setForm(emptyForm()); setOpenForm(true); };
  const openEdit = (p) => {
    setEditing(p);
    setForm({
      name: p.name, code: p.code, client_name: p.client_name || "",
      product_unit_name: p.product_unit_name || "Cup", product_price: p.product_price || 9,
      target_per_store: p.target_per_store || 400, geofence_m: p.geofence_m || 150,
    });
    setOpenForm(true);
  };

  const save = async () => {
    if (!form.name.trim()) return toast.error("Name is required");
    try {
      if (editing) {
        const patch = { ...form };
        delete patch.code; // code immutable
        await api.patch(`/projects/${editing.id}`, patch);
        toast.success("Project updated");
      } else {
        await api.post("/projects", form);
        toast.success("Project created");
      }
      setOpenForm(false);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const del = async () => {
    if (!confirmDel) return;
    try {
      await api.delete(`/projects/${confirmDel.id}`);
      toast.success("Project deleted");
      setConfirmDel(null);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <AppHeader title="Projects" subtitle="Multi-project management" />
      <main className="max-w-5xl mx-auto px-4 sm:px-6 py-6 space-y-4">
        <Card className="p-5 rounded-xl border-gray-200 bg-white flex items-center justify-between">
          <div>
            <div className="text-xs font-bold uppercase tracking-[0.1em] text-gray-500">Projects</div>
            <div className="font-display text-xl font-bold text-gray-900 mt-0.5">{projects.length} project{projects.length === 1 ? "" : "s"}</div>
            <div className="text-[11px] text-gray-500 mt-1">Each project has its own stores, users, data & branding.</div>
          </div>
          {isSuper && (
            <Button data-testid="new-project-btn" onClick={openNew} className="bg-brand-red hover:bg-[#B31721] text-white">
              <Plus className="h-4 w-4 mr-1.5" /> New project
            </Button>
          )}
        </Card>

        {loading ? <div className="p-8 text-center text-gray-500">Loading…</div>
          : projects.length === 0 ? (
            <Card className="p-10 rounded-xl border-dashed border-gray-300 text-center bg-white">
              <FolderKanban className="h-8 w-8 mx-auto text-gray-400" />
              <div className="mt-2 text-sm text-gray-500">No projects yet.</div>
            </Card>
          ) : (
            <div className="space-y-3" data-testid="projects-list">
              {projects.map((p) => (
                <Card key={p.id} className="p-4 rounded-xl border-gray-200 bg-white" data-testid={`project-${p.code || p.id.slice(0,8)}`}>
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-semibold text-gray-900 text-lg">{p.name}</span>
                        {p.is_default && <Badge className="bg-gray-100 text-gray-600 border border-gray-200 text-[10px]">Default</Badge>}
                        <span className="text-xs text-gray-500">{p.code}</span>
                      </div>
                      <div className="mt-2 grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
                        <div><span className="text-gray-500">Client:</span> <span className="font-semibold">{p.client_name || "—"}</span></div>
                        <div><span className="text-gray-500">Product:</span> <span className="font-semibold">{p.product_unit_name} × ₹{p.product_price}</span></div>
                        <div><span className="text-gray-500">Target/store:</span> <span className="font-semibold">{p.target_per_store}</span></div>
                        <div><span className="text-gray-500">Geofence:</span> <span className="font-semibold">{p.geofence_m}m</span></div>
                      </div>
                    </div>
                    {isSuper && (
                      <div className="flex items-center gap-1 shrink-0">
                        <button onClick={() => openEdit(p)} title="Edit"
                          className="h-8 w-8 rounded-lg hover:bg-gray-100 flex items-center justify-center text-gray-600"
                          data-testid={`edit-project-${p.code || p.id.slice(0,8)}`}>
                          <Pencil className="h-4 w-4" />
                        </button>
                        {!p.is_default && (
                          <button onClick={() => setConfirmDel(p)} title="Delete"
                            className="h-8 w-8 rounded-lg hover:bg-red-50 flex items-center justify-center text-red-600"
                            data-testid={`del-project-${p.code || p.id.slice(0,8)}`}>
                            <Trash2 className="h-4 w-4" />
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                </Card>
              ))}
            </div>
          )}
      </main>

      <Dialog open={openForm} onOpenChange={setOpenForm}>
        <DialogContent data-testid="project-dialog" className="rounded-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editing ? "Edit project" : "New project"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <label className="text-xs font-bold uppercase tracking-[0.1em] text-gray-500">Name</label>
              <Input data-testid="project-name" placeholder="e.g. Pepsi @9 — Reliance MP" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
            </div>
            {!editing && (
              <div>
                <label className="text-xs font-bold uppercase tracking-[0.1em] text-gray-500">Code (immutable)</label>
                <Input data-testid="project-code" placeholder="e.g. PEPSI9-MP" value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase().replace(/\s+/g, "-") })} />
              </div>
            )}
            <div>
              <label className="text-xs font-bold uppercase tracking-[0.1em] text-gray-500">Client name</label>
              <Input data-testid="project-client-name" placeholder="e.g. Pepsi India" value={form.client_name} onChange={(e) => setForm({ ...form, client_name: e.target.value })} />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-bold uppercase tracking-[0.1em] text-gray-500">Product unit</label>
                <Input data-testid="project-unit" placeholder="Cup" value={form.product_unit_name} onChange={(e) => setForm({ ...form, product_unit_name: e.target.value })} />
              </div>
              <div>
                <label className="text-xs font-bold uppercase tracking-[0.1em] text-gray-500">Price ₹</label>
                <Input data-testid="project-price" type="number" value={form.product_price} onChange={(e) => setForm({ ...form, product_price: Number(e.target.value) })} />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-bold uppercase tracking-[0.1em] text-gray-500">Target per store/day</label>
                <Input data-testid="project-target" type="number" value={form.target_per_store} onChange={(e) => setForm({ ...form, target_per_store: Number(e.target.value) })} />
              </div>
              <div>
                <label className="text-xs font-bold uppercase tracking-[0.1em] text-gray-500">Geofence (metres)</label>
                <Input data-testid="project-geofence" type="number" value={form.geofence_m} onChange={(e) => setForm({ ...form, geofence_m: Number(e.target.value) })} />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpenForm(false)}>Cancel</Button>
            <Button data-testid="save-project-btn" onClick={save} className="bg-brand-red hover:bg-[#B31721] text-white">
              {editing ? "Save" : "Create project"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!confirmDel} onOpenChange={(v) => !v && setConfirmDel(null)}>
        <DialogContent data-testid="project-del-dialog" className="rounded-2xl">
          <DialogHeader><DialogTitle>Delete project?</DialogTitle></DialogHeader>
          <div className="text-sm text-gray-700">
            <p>This soft-deletes <span className="font-semibold">{confirmDel?.name}</span>. Underlying stores/users/data are preserved but the project will no longer appear in the switcher.</p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmDel(null)}>Cancel</Button>
            <Button data-testid="confirm-del-project" onClick={del} className="bg-red-600 hover:bg-red-700 text-white">
              <Trash2 className="h-4 w-4 mr-1.5" /> Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
